import * as React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { kitT, type KitTranslateFn } from '../../lib/kitTranslate';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import type { ColorSwatchOption } from './ColorPalette';

export interface ColorPaletteEditorProps {
    value: ColorSwatchOption[];
    onChange: (options: ColorSwatchOption[]) => void;
    minItems?: number;
    maxItems?: number;
    className?: string;
    translate?: KitTranslateFn;
}

export const ColorPaletteEditor: React.FC<ColorPaletteEditorProps> = ({
    value,
    onChange,
    minItems = 2,
    maxItems = 12,
    className,
    translate,
}) => {
    const tx = (key: string, fb: string) => kitT(translate, key, fb);

    const handleAdd = () => {
        if (value.length >= maxItems) return;
        const id = `color-${Date.now()}`;
        const nextIndex = value.length + 1;
        const labelTpl = tx('kCpeNewColorLabel', 'Color {n}');
        const label = labelTpl.replace('{n}', String(nextIndex));
        onChange([...value, { id, label, value: '#3B82F6' }]);
    };

    const handleRemove = (id: string) => {
        if (value.length <= minItems) return;
        onChange(value.filter((o) => o.id !== id));
    };

    const handleChange = (id: string, updates: Partial<ColorSwatchOption>) => {
        onChange(
            value.map((o) =>
                o.id === id ? { ...o, ...updates } : o
            )
        );
    };

    return (
        <div className={cn('space-y-2', className)}>
            <div className="flex items-center justify-between">
                <Label>{tx('kCpePalette', 'Color palette')}</Label>
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={handleAdd}
                    disabled={value.length >= maxItems}
                    className="gap-1"
                >
                    <Plus className="h-4 w-4" />
                    {tx('kCpeAdd', 'Add')}
                </Button>
            </div>
            <div className="space-y-2">
                {value.map((opt) => (
                    <div
                        key={opt.id}
                        className="flex items-center gap-2 rounded-sm border border-border bg-muted/30 p-2"
                    >
                        <input
                            type="color"
                            value={opt.value}
                            onChange={(e) =>
                                handleChange(opt.id, { value: e.target.value })
                            }
                            className="h-9 w-9 cursor-pointer rounded border border-border bg-background p-0"
                        />
                        <Input
                            value={opt.label}
                            onChange={(e) =>
                                handleChange(opt.id, { label: e.target.value })
                            }
                            placeholder={tx('kCpeLabelPlaceholder', 'Label (e.g. Red)')}
                            className="flex-1"
                        />
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemove(opt.id)}
                            disabled={value.length <= minItems}
                            aria-label={tx('kCpeRemove', 'Remove color')}
                            className="shrink-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                ))}
            </div>
            <p className="text-xs text-muted-foreground">
                {tx('kCpeColorCount', '{current} of {max} colors')
                    .replace('{current}', String(value.length))
                    .replace('{max}', String(maxItems))}
            </p>
        </div>
    );
};

ColorPaletteEditor.displayName = 'ColorPaletteEditor';
